import { FC, useMemo, useState } from "react";
import {
  MsgExecute,
  MsgRedactedGasTankWithdraw,
  signers,
  TxResult,
} from "rujira.js";
import {
  Button,
  Decimal,
  DenomInput,
  Icons,
  IconDenom,
  trim0x,
  Warning,
} from "rujira.ui";
import { MsgProvider, TxButton } from "../../common/components/TxButton";
import { useGetEncStealth } from "../../redacted";
import { REDACTED_PROXY_CONTRACT } from "../../redacted/config";
import { useRedacted } from "../../services/redacted";

const SWAP_GAS_AMOUNT = 2_000_000n;

type GasTankDepositProps = {
  balance: bigint;
  walletBalance: bigint;
  runePrice?: bigint | null;
  onCancel: () => void;
  onSuccess?: (tx: TxResult) => void;
};

/**
 * Compatibility-contract swap-gas funding UI. It intentionally uses the
 * shared Rujira modal, amount input, buttons and transaction plumbing so it
 * behaves like the existing deposit/withdraw flows.
 */
export const GasTankDeposit: FC<GasTankDepositProps> = ({
  balance,
  walletBalance,
  runePrice,
  onCancel,
  onSuccess,
}) => {
  const { isPrivateMode, publicAddress, stealth } = useRedacted();
  const encStealthData = useGetEncStealth(stealth);
  const [activeTab, setActiveTab] = useState<"deposit" | "withdraw">("deposit");
  const [amount, setAmount] = useState(0n);
  const max = activeTab === "deposit" ? walletBalance : balance;
  const estimatedSwaps = balance / SWAP_GAS_AMOUNT;
  const hasValidProjection = amount > 0n && amount <= max;
  const projectedBalance =
    activeTab === "deposit"
      ? balance + amount
      : amount <= balance
        ? balance - amount
        : 0n;
  const projectedSwaps = projectedBalance / SWAP_GAS_AMOUNT;

  const msg = useMemo(() => {
    if (!publicAddress || !stealth || amount <= 0n || amount > max) return null;

    if (activeTab === "deposit") {
      if (!encStealthData) return null;
      return new MsgExecute(
        { address: publicAddress, network: "THOR" },
        signers.cosmos.coins(amount.toString(), "rune"),
        REDACTED_PROXY_CONTRACT,
        {
          deposit_to_gas_tank: {
            stealth: trim0x(encStealthData.stealth),
            t: encStealthData.t,
          },
        }
      );
    }

    if (!isPrivateMode) return null;
    return new MsgRedactedGasTankWithdraw(
      { address: publicAddress, network: "THOR" },
      stealth,
      amount.toString(),
      "rune"
    );
  }, [
    activeTab,
    amount,
    encStealthData,
    isPrivateMode,
    max,
    publicAddress,
    stealth,
  ]);

  const selectTab = (tab: "deposit" | "withdraw") => {
    setActiveTab(tab);
    setAmount(0n);
  };

  return (
    <MsgProvider msg={msg}>
      <h3 className="h3 mb-0 flex ai-c">
        <IconDenom denom="RUNE" className="as-c w-4 h-4 block mr-1 no-shrink" />
        Swap Gas Tank
      </h3>

      <Warning
        color="primary5"
        className="warning--sm mt-2 condensed flex ai-c jc-c">
        <Icons.ExclamationCircle className="w-2 h-2 no-shrink" />
        <span className="warning__msg" style={{ textAlign: "center" }}>
          Private swaps use Gas Tank RUNE for THORChain network fees.
        </span>
        <span aria-hidden="true" className="w-2 h-2 no-shrink" />
      </Warning>

      <div className="card p-2 mt-2 flex ai-c jc-sb gap-x-1">
        <span className="condensed color-grey">Current balance</span>
        <div className="flex dir-c ai-e">
          <Decimal
            amount={balance}
            decimals={8}
            symbol="RUNE"
            className="fw-700 color-white"
          />
          <small className="color-teal mt-0.5">
            Approximately {estimatedSwaps.toString()} swaps remaining
          </small>
        </div>
      </div>

      <div className="flex mt-2 gap-x-1">
        <Button
          type="button"
          className={
            activeTab === "deposit"
              ? "grow"
              : "grow button--outline button--grey"
          }
          label="Deposit"
          onClick={() => selectTab("deposit")}
        />
        <Button
          type="button"
          className={
            activeTab === "withdraw"
              ? "grow"
              : "grow button--outline button--grey"
          }
          label="Withdraw"
          onClick={() => selectTab("withdraw")}
        />
      </div>

      <DenomInput
        symbol="RUNE"
        className="mt-2"
        amount={amount}
        decimals={8}
        onChangeAmount={setAmount}
        max={max}
        maxLabel="MAX"
        fiat={{ price: runePrice ?? undefined, symbol: "$" }}
      />

      {hasValidProjection && (
        <div className="mt-1 px-1 condensed fs-12 color-grey text-center">
          After {activeTab === "deposit" ? "deposit" : "withdrawal"},{" "}
          <span className="color-white fw-700">
            approximately {projectedSwaps.toString()} private swaps remaining
          </span>
        </div>
      )}

      {max === 0n && (
        <Warning
          color="orange"
          className="warning--sm mt-1 condensed flex ai-c jc-c">
          <Icons.ExclamationCircle className="w-2 h-2 no-shrink" />
          <span className="warning__msg" style={{ textAlign: "center" }}>
            {activeTab === "deposit"
              ? "No RUNE available in the connected public account."
              : "No RUNE available in the Gas Tank."}
          </span>
          <span aria-hidden="true" className="w-2 h-2 no-shrink" />
        </Warning>
      )}

      <div className="modal__footer mt-4 px-3 py-2 flex ai-s jc-sb gap-x-1">
        <Button
          type="button"
          className="button--grey button--outline"
          onClick={onCancel}
          label="Cancel"
        />
        <div className="flex dir-c ai-c ml-a">
          <TxButton
            label={
              activeTab === "deposit"
                ? "Deposit to Gas Tank"
                : "Withdraw from Gas Tank"
            }
            submitPublic={activeTab === "deposit"}
            disabled={!msg || amount <= 0n || amount > max}
            onSuccess={(tx) => {
              setAmount(0n);
              onSuccess?.(tx);
            }}
          />
        </div>
      </div>
    </MsgProvider>
  );
};

export default GasTankDeposit;
